import { ReactNode } from 'react';
import { DeliveryDTO } from '../../api/generated';
import i18n from '../../i18n';
import { getNestedProperty } from '../../utils';

export type DisplayConfigItem = {
  key: string;
  label: string;
  value?: (d: DeliveryDTO) => ReactNode;
};

const defaultDisplayConfig: DisplayConfigItem[] = [
  { key: 'delivery_id', label: 'delivery.delivery_id' },
  {
    key: 'material',
    label: 'delivery.material',
    value: (d) => [d.material_id, d.material].filter(Boolean).join(' '),
  },
  { key: 'avv', label: 'delivery.avv' },
  {
    key: 'date',
    label: 'delivery.date',
    value: (d) => (d.date ? new Date(d.date).toLocaleDateString(i18n.language) : undefined),
  },
  {
    key: 'time',
    label: 'delivery.time',
    value: (d) => (d.date ? new Date(d.date).toLocaleTimeString(i18n.language) : undefined),
  },
  {
    key: 'netto_weight',
    label: 'delivery.netto_weight',
    value: (d) => (d.netto_weight !== null && d.netto_weight !== undefined ? `${d.netto_weight} kg` : undefined),
  },
  { key: 'construction_site', label: 'delivery.construction_site' },
  { key: 'numberplate', label: 'delivery.numberplate' },
  { key: 'data.driver_name', label: 'delivery.data.driver_name' },
  { key: 'detail', label: 'delivery.detail' },
  { key: 'supplier', label: 'delivery.supplier' },
];

const doernerDisplayConfig: DisplayConfigItem[] = [
  { key: 'delivery_id', label: 'delivery.doerner.wiegenoteNummer' },
  {
    key: 'time',
    label: 'delivery.doerner.time',
    value: (d) => {
      const wiegezeitpunkt = getNestedProperty(d, 'data.wiegezeitpunkt');
      if (wiegezeitpunkt) {
        return new Date(wiegezeitpunkt).toLocaleString(i18n.language);
      }
      return d.date ? new Date(d.date).toLocaleTimeString(i18n.language) : undefined;
    },
  },
  { key: 'numberplate', label: 'delivery.numberplate' },
  { key: 'data.wage_artikelnummer', label: 'delivery.doerner.wiegenoteArtikelNummer' },
  { key: 'data.wage_artikelbezeichnung', label: 'delivery.doerner.wiegenoteArtikelbezeichnung' },
  { key: 'data.lieferabruf_avv_nummer', label: 'delivery.doerner.lieferabrufAvvNummer' },
  { key: 'data.lieferabruf_artikelnummer', label: 'delivery.doerner.lieferabrufArtikelnummer' },
  { key: 'data.lieferabruf_artikelbezeichnung', label: 'delivery.doerner.lieferabrufArtikelbezeichnung' },
  { key: 'data.lieferabruf_mainuid', label: 'delivery.doerner.lieferabrufMainUid' },
  { key: 'data.lieferabruf_subuid', label: 'delivery.doerner.lieferabrufSubUid' },
  { key: 'data.tour_nummer', label: 'delivery.doerner.tourNummer' },
  { key: 'data.tour_auftragsnummer', label: 'delivery.doerner.tourAuftragsNummer' },
  {
    key: 'netto_weight',
    label: 'delivery.netto_weight',
    value: (d) => (d.netto_weight ? `${d.netto_weight} kg` : undefined),
  },
  { key: 'construction_site', label: 'delivery.construction_site' },
  { key: 'supplier', label: 'delivery.supplier' },
];

const mennerDisplayConfig: DisplayConfigItem[] = [
  { key: 'delivery_id', label: 'delivery.delivery_id' },
  { key: 'data.ewcNumber', label: 'delivery.avv' },
  {
    key: 'data.ewcLabel',
    label: 'delivery.material',
    value: (d: any) => (d.data.ewcLabel ? i18n.t(`material.class.${d.data.ewcLabel}`) : undefined),
  },
  {
    key: 'data.hasEwcOverwrite',
    label: 'delivery.has_ewc_overwrite',
    value: (d: any) =>
      d.data.hasEwcOverwrite !== undefined
        ? d.data.hasEwcOverwrite
          ? i18n.t('common.yes')
          : i18n.t('common.no')
        : undefined,
  },
  {
    key: 'data.hasImageApproved',
    label: 'delivery.has_image_marked_wrong',
    value: (d: any) =>
      d.data.hasImageApproved !== undefined
        ? !d.data.hasImageApproved
          ? i18n.t('common.yes')
          : i18n.t('common.no')
        : undefined,
  },
  {
    key: 'date',
    label: 'delivery.date',
    value: (d) => (d.date ? new Date(d.date).toLocaleDateString() : undefined),
  },
  {
    key: 'time',
    label: 'delivery.time',
    value: (d) => (d.date ? new Date(d.date).toLocaleTimeString() : undefined),
  },
  { key: 'netto_weight', label: 'delivery.netto_weight' },
  { key: 'construction_site', label: 'delivery.construction_site' },
  { key: 'numberplate', label: 'delivery.numberplate' },
  { key: 'data.driver_name', label: 'delivery.data.driver_name' },

  { key: 'detail', label: 'delivery.detail' },
];

const capturerDisplayConfigs: Record<string, DisplayConfigItem[]> = {
  'occapture-doerner01': doernerDisplayConfig,
  'occapture-menner01': mennerDisplayConfig,
};

export function getDisplayConfig(capturer_id?: string): DisplayConfigItem[] {
  if (capturer_id && capturerDisplayConfigs[capturer_id]) {
    return capturerDisplayConfigs[capturer_id];
  }
  return defaultDisplayConfig;
}

export function extractDeliveryEntries(
  delivery: DeliveryDTO,
  capturer_id?: string
): Array<{ key: string; label: string; value: any }> {
  const config = getDisplayConfig(capturer_id);

  return config
    .map(({ key, label, value: valueFn }) => {
      const value = valueFn ? valueFn(delivery) : getNestedProperty(delivery, key);
      return { key, label, value };
    })
    .filter((entry) => entry.value !== null && entry.value !== undefined && entry.value !== '');
}
